<template>
    <div class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-content wide-modal">
            <button class="close-button" @click="$emit('close')">&times;</button>
            
            <!-- Member Status Section -->
            <div class="status-section">
                <h3>Member Status</h3>
                <div class="status-controls">
                    <div class="status-toggle">
                        <label class="switch">
                            <input 
                                type="checkbox" 
                                :checked="isUserActive"
                                @change="toggleStatus"
                            >
                            <span class="slider"></span>
                        </label>
                        <span class="status-label">{{ userStatus }}</span>
                    </div>
                    
                    <div v-if="!isUserActive" class="inactive-details">
                        <div class="inactive-date">
                            Inactive since: {{ formatDate(selectedUser.inactive_date) }}
                        </div>
                        <div v-if="selectedUser.status_notes" class="status-notes-display">
                            Reason: {{ selectedUser.status_notes }}
                        </div>
                    </div>
                </div>

                <div v-if="showStatusNotes" class="status-notes">
                    <label for="statusNotes">Reason for status change:</label>
                    <textarea
                        id="statusNotes"
                        v-model="statusNotes"
                        class="form-control"
                        rows="3"
                        placeholder="Enter the reason for changing the member's status..."
                    ></textarea>
                    <div class="status-actions">
                        <button class="cancel-btn" @click="cancelStatusChange">Cancel</button>
                        <button class="confirm-btn" @click="confirmStatusChange">Confirm Change</button>
                    </div>
                </div>
            </div>

            <!-- Credentials Management Section (Admin Only) -->
            <div v-if="currentUser && currentUser.role === 'admin'" class="credentials-section">
                <h3>Update Credentials</h3>
                <div class="credentials-form">
                    <div class="credential-field">
                        <label for="userEmail">Email</label>
                        <input
                            id="userEmail"
                            v-model="userEmail"
                            type="email"
                            class="credential-input"
                            placeholder="Email address"
                            :disabled="updatingCredentials"
                        >
                    </div>

                    <div class="credential-field">
                        <label for="newPassword">Password</label>
                        <div class="password-input-wrapper">
                            <input
                                id="newPassword"
                                v-model="newPassword"
                                :type="showPassword ? 'text' : 'password'"
                                class="credential-input"
                                placeholder="New password (optional)"
                                :disabled="updatingCredentials"
                            >
                            <button
                                type="button"
                                @click="showPassword = !showPassword"
                                class="password-toggle"
                                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                            >
                                <span v-if="showPassword">👁️</span>
                                <span v-else>👁️‍🗨️</span>
                            </button>
                        </div>
                    </div>

                    <div class="credential-button-wrapper">
                        <button
                            class="update-credentials-btn"
                            @click="updateCredentials"
                            :disabled="updatingCredentials"
                        >
                            {{ updatingCredentials ? 'Updating...' : 'Update' }}
                        </button>
                    </div>
                </div>
                <div v-if="credentialsMessage" class="credentials-message" :class="credentialsMessageType">
                    {{ credentialsMessage }}
                </div>
            </div>

            <UserProfile
                :current-user-id="currentUserId"
                :target-user-id="selectedUser.id.toString()"
                :target-db-id="selectedUser.id"
                :userData="selectedUser"
                @close="$emit('close')"
                @updated="handleUserUpdated"
                modal-mode
            />
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import UserProfile from './UserProfile.vue';
import { userService } from '../services/api';

export default defineComponent({
    name: 'AdminEditUser',
    components: {
        UserProfile
    },
    props: {
        selectedUserId: {
            type: String,
            required: true
        },
        selectedUser: {
            type: Object,
            required: true
        }
    },
    emits: ['close', 'user-updated'],
    setup(props, { emit }) {

        const currentUserId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).id.toString() : null;
        const currentUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
        const showStatusNotes = ref(false);
        const statusNotes = ref('');
        const pendingStatus = ref(null);
        const userEmail = ref(props.selectedUser.email || '');
        const newPassword = ref('');
        const updatingCredentials = ref(false);
        const credentialsMessage = ref('');
        const credentialsMessageType = ref('');
        const showPassword = ref(false);

        // Computed properties for status
        const isUserActive = computed(() => {
            return props.selectedUser.status !== 'inactive';
        });

        const userStatus = computed(() => {
            return props.selectedUser.status === 'inactive' ? 'Inactive' : 'Active';
        });

        const formatDate = (dateString) => {
            if (!dateString) return '';
            return new Date(dateString).toLocaleDateString('en-AU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        };

        const toggleStatus = () => {
            const currentStatus = props.selectedUser.status || 'active';
            pendingStatus.value = currentStatus === 'inactive' ? 'active' : 'inactive';
            showStatusNotes.value = true;
        };

        const cancelStatusChange = () => {
            showStatusNotes.value = false;
            statusNotes.value = '';
            pendingStatus.value = null;
        };

        const confirmStatusChange = async () => {
            try {
                
                const result = await userService.updateMemberStatus(
                    props.selectedUser.id,
                    pendingStatus.value,
                    statusNotes.value
                );
                
                
                // Update the local user data and emit the change using the server response
                const updatedUser = {
                    ...props.selectedUser,
                    ...result  // Use the server response data
                };
                
                emit('user-updated', updatedUser);
                
                showStatusNotes.value = false;
                statusNotes.value = '';
                pendingStatus.value = null;
            } catch (error) {
                console.error('Error updating member status:', error);
                // You might want to show an error message to the user here
            }
        };

        const handleUserUpdated = (userData) => {
            emit('user-updated', userData);
            emit('close');
        };

        const updateCredentials = async () => {
            // Validate at least one field is being updated
            if (!userEmail.value && !newPassword.value) {
                credentialsMessage.value = 'Please enter an email or password to update';
                credentialsMessageType.value = 'error';
                return;
            }

            // Validate email format
            if (userEmail.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.value)) {
                credentialsMessage.value = 'Please enter a valid email address';
                credentialsMessageType.value = 'error';
                return;
            }

            updatingCredentials.value = true;
            credentialsMessage.value = '';
            credentialsMessageType.value = '';

            try {
                const updates = {};
                const updateMessages = [];

                // Update email if changed
                if (userEmail.value && userEmail.value !== props.selectedUser.email) {
                    updates.email = userEmail.value;
                    updateMessages.push('email');
                }

                // Update password if provided
                if (newPassword.value) {
                    await userService.setUserPassword(
                        props.selectedUser.id,
                        newPassword.value
                    );
                    updateMessages.push('password');
                    newPassword.value = '';
                }

                // Update user profile with email change
                if (updates.email) {
                    await userService.updateUser(props.selectedUser.id, updates);
                }

                const updatedFields = updateMessages.join(' and ');
                credentialsMessage.value = `Successfully updated ${updatedFields} for ${props.selectedUser.first_name} ${props.selectedUser.last_name}.`;
                credentialsMessageType.value = 'success';

                // Emit user updated event
                if (updates.email) {
                    emit('user-updated', { ...props.selectedUser, ...updates });
                }

                // Clear message after 5 seconds
                setTimeout(() => {
                    credentialsMessage.value = '';
                    credentialsMessageType.value = '';
                }, 5000);
            } catch (error) {
                console.error('Error updating credentials:', error);
                credentialsMessage.value = error.response?.data?.error || 'Failed to update credentials. Please try again.';
                credentialsMessageType.value = 'error';
            } finally {
                updatingCredentials.value = false;
            }
        };

        return {
            handleUserUpdated,
            currentUserId,
            currentUser,
            showStatusNotes,
            statusNotes,
            toggleStatus,
            cancelStatusChange,
            confirmStatusChange,
            formatDate,
            isUserActive,
            userStatus,
            userEmail,
            newPassword,
            updatingCredentials,
            credentialsMessage,
            credentialsMessageType,
            showPassword,
            updateCredentials
        };
    }
});
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 20px 30px;
    border-radius: 8px;
    width: 900px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
}

.wide-modal {
    margin: 20px auto;
    max-width: 95%;
}

.close-button {
    position: absolute;
    right: 15px;
    top: 15px;
    border: none;
    background: none;
    font-size: 24px;
    cursor: pointer;
    z-index: 1;
}

.status-section {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
}

.status-controls {
    display: flex;
    align-items: center;
    gap: 20px;
    margin: 15px 0;
}

.status-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
}

.switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #dc3545;
    transition: .4s;
    border-radius: 34px;
}

.slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
}

input:checked + .slider {
    background-color: #198754;
}

input:checked + .slider:before {
    transform: translateX(26px);
}

.status-label {
    font-weight: 500;
}

.inactive-details {
    color: #6c757d;
    font-size: 0.9em;
}

.status-notes {
    margin-top: 15px;
}

.status-notes textarea {
    width: 100%;
    margin: 10px 0;
    padding: 10px;
    border: 1px solid #ced4da;
    border-radius: 4px;
}

.status-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 10px;
}

.cancel-btn {
    padding: 8px 16px;
    border: 1px solid #6c757d;
    background-color: transparent;
    color: #6c757d;
    border-radius: 4px;
    cursor: pointer;
}

.confirm-btn {
    padding: 8px 16px;
    border: none;
    background-color: #0d6efd;
    color: white;
    border-radius: 4px;
    cursor: pointer;
}

.confirm-btn:hover {
    background-color: #0b5ed7;
}

.cancel-btn:hover {
    background-color: #6c757d;
    color: white;
}

.credentials-section {
    background-color: #f8f9fa;
    border-radius: 6px;
    padding: 12px 15px;
    margin-bottom: 15px;
}

.credentials-section h3 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
}

.credentials-form {
    display: flex;
    gap: 15px;
    align-items: flex-end;
}

.credential-field {
    flex: 0 0 auto;
    width: 220px;
}

.credential-field label {
    display: block;
    margin-bottom: 3px;
    color: #2c3e50;
    font-weight: 500;
    font-size: 12px;
}

.credential-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 13px;
    box-sizing: border-box;
}

.credential-button-wrapper {
    flex-shrink: 0;
    display: flex;
    align-items: flex-end;
    margin-left: 10px;
}

.password-input-wrapper {
    position: relative;
    display: block;
    width: auto;
}

.password-input-wrapper .credential-input {
    padding-right: 32px;
}

.password-toggle {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 6px;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
    transition: opacity 0.2s;
}

.password-toggle:hover {
    opacity: 1;
}

.update-credentials-btn {
    padding: 6px 12px;
    background-color: #0d6efd;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    white-space: nowrap;
    height: 30px;
}

.update-credentials-btn:hover {
    background-color: #0b5ed7;
}

.update-credentials-btn:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
}

.credential-input:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
}

.credentials-message {
    margin-top: 8px;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
}

.credentials-message.success {
    background-color: #d1e7dd;
    color: #0f5132;
    border: 1px solid #badbcc;
}

.credentials-message.error {
    background-color: #f8d7da;
    color: #842029;
    border: 1px solid #f5c2c7;
}
</style>